import { api } from '@/api/client';
import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { Card, CardContent, toast } from '@kgc/ui';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import { useState } from 'react';
import { RejectApprovalDialog } from '../dialogs/RejectApprovalDialog';
import type { ApprovalRequest } from '../types';

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface ApprovalCardProps {
  approval: ApprovalRequest;
  onResolved?: (() => void) | undefined;
}

export function ApprovalCard({ approval, onResolved }: ApprovalCardProps) {
  const { t } = useTranslation();
  const [isApproving, setIsApproving] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  async function handleApprove() {
    setIsApproving(true);
    try {
      await api.post(`/approvals/${approval.id}/approve`);
      toast.success(t('integration.flow.approval.approved'));
      onResolved?.();
    } catch {
      toast.error(t('integration.flow.approval.error'));
    } finally {
      setIsApproving(false);
    }
  }

  const isPending = approval.status === 'pending' || approval.status === 'requested';

  return (
    <Card data-testid={`flow-approval-card-${approval.id}`}>
      <CardContent className="py-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-yellow-500" aria-hidden="true" />
            <span className="font-medium text-sm text-foreground">
              {approval.entityType} #{approval.entityId}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">{formatTime(approval.requestedAt)}</span>
        </div>

        <div className="text-xs text-muted-foreground space-y-0.5">
          <p>
            {t('integration.flow.approval.requestedBy')}: {approval.requestedBy}
          </p>
          <p>
            {t('integration.flow.approval.requiredRole')}: {approval.requiredRole}
          </p>
          {approval.reason && (
            <p>
              {t('integration.flow.approval.reason')}: {approval.reason}
            </p>
          )}
        </div>

        {isPending && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={() => void handleApprove()}
              disabled={isApproving}
              data-testid={`flow-approve-btn-${approval.id}`}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-1" aria-hidden="true" />
              {t('integration.flow.approval.approve')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowRejectDialog(true)}
              data-testid={`flow-reject-btn-${approval.id}`}
            >
              <XCircle className="h-4 w-4 mr-1" aria-hidden="true" />
              {t('integration.flow.approval.reject')}
            </Button>
          </div>
        )}
      </CardContent>

      <RejectApprovalDialog
        approvalId={approval.id}
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        onRejected={() => {
          setShowRejectDialog(false);
          onResolved?.();
        }}
      />
    </Card>
  );
}
